import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Paper,
    TextField,
    Button,
    Typography,
    CircularProgress,
    Modal,
    Tooltip,
    IconButton,
    Snackbar,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Drawer,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/system";
import axios from "axios";
import FileUploadIcon from "@mui/icons-material/UploadFile";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import languages from "../../data/languages";

// ----------------------------------------------
// Import all images from src/assets
import AIChatGirl from "../../assets/AIChatGirl.jpg";
import arnoldArms from "../../assets/arnold_arms.jpg";
import figAgent from "../../assets/fig_agent.png";
import figCROP from "../../assets/fig_CROP.png";
import googleLogo from "../../assets/google_logo.png";
import gs1Logo from "../../assets/gs1_logo.png";
import liquidofficeLogo from "../../assets/liquidoffice_logo.jpg";
import mcsLogo from "../../assets/mcs_logo.jpg";
import mosaicLogo from "../../assets/mosaic_logo.jpg";
import ocpsjogo from "../../assets/ocps_logo.png";
import proteam from "../../assets/proteam.png";
import smartsheetjogo from "../../assets/smartsheet_logo.png";
import swanAiLab from "../../assets/SWAN-Ai-Lab_.png";
import usdaLogo from "../../assets/usda-logo.png";
import usda_logo from "../../assets/usda_logo.png";
import threePFitness from "../../assets/3pfitness.jpg";
import vidhanLogo from "../../assets/vidhanlogo.jpg";

// Build a mapping from file names to image imports.
const logoImages = {
    "AIChatGirl.jpg": AIChatGirl,
    "arnold_arms.jpg": arnoldArms,
    "fig_agent.png": figAgent,
    "fig_CROP.png": figCROP,
    "google_logo.png": googleLogo,
    "gs1_logo.png": gs1Logo,
    "liquidoffice_logo.jpg": liquidofficeLogo,
    "mcs_logo.jpg": mcsLogo,
    "mosaic_logo.jpg": mosaicLogo,
    "ocps_ogo.png": ocpsjogo,
    "proteam.png": proteam,
    "smartsheet_logo.png": smartsheetjogo,
    "SWAN-Ai-Lab_.png": swanAiLab,
    "usda-logo.png": usdaLogo,
    "usda_logo.png": usda_logo,
    "3pfitness.jpg": threePFitness,
    "vidhanlogo.jpg": vidhanLogo,
};

// Fallback API key if pageData does not provide one.
const FALLBACK_API_KEY =
    "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";

// ----------------------------------------------
// Styled Components

// Updated to fill the viewport height.
const LayoutContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    height: "100vh", // changed from "100%"
    width: "100%",
    position: "relative", // for floating button positioning
}));

// IFrame remains the same.
const IFrameContainer = styled(Box)(({ theme }) => ({
    flex: 1,
    border: "none",
    display: "flex",
    flexDirection: "column",
}));

// Updated ChatContainer to have a fixed height so it does not grow with chat messages.
// It accepts an "isMobile" prop to adjust the height for mobile (100%) vs. desktop (100vh).
const ChatContainer = styled(Box)(({ theme, isMobile }) => ({
    width: 350,
    height: isMobile ? "100%" : "100vh", // fixed height for desktop; full parent's height on mobile
    overflow: "hidden", // prevents expanding � inner container will scroll
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#E3F2FD", // Light blue
    color: "#000000", // Dark text
    borderLeft: "1px solid #CCC",
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
    padding: "16px",
    borderBottom: "1px solid #CCC",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    position: "relative",
}));

const Logo = styled("img")({
    width: "80px",
    height: "auto",
    objectFit: "contain",
});

const TitleText = styled(Typography)({
    fontWeight: "bold",
    color: "#000",
    textAlign: "center",
});

// ChatMessages container will scroll internally.
const ChatMessages = styled(Box)(({ theme }) => ({
    flex: 1,
    overflowY: "auto",
    padding: "16px",
}));

const ChatBubble = styled(Paper)(({ isUser }) => ({
    padding: "10px 12px",
    borderRadius: "12px",
    maxWidth: "80%",
    color: isUser ? "#fff" : "#000",
    backgroundColor: isUser ? "#90CAF9" : "#FFFFFF",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    cursor: "default",
    display: "flex",
    alignItems: "center",
    gap: "8px",
}));

const ChatInputArea = styled(Box)(({ theme }) => ({
    padding: "16px",
    borderTop: "1px solid #CCC",
}));

// ----------------------------------------------
// Provider Configuration Object
const providerConfigs = {
    openai: {
        endpoint: "https://api.openai.com/v1/chat/completions",
        getHeaders: (apiKey) => ({
            Authorization: `Bearer ${apiKey}`,
        }),
        getPayload: (model, conversationMessages) => ({
            model,
            messages: conversationMessages,
            max_tokens: 500,
            temperature: 0.7,
        }),
    },
    azure: {
        endpoint:
            "https://your-azure-endpoint/openai/deployments/your-deployment/chat/completions?api-version=2023-03-15-preview",
        getHeaders: (apiKey) => ({
            "api-key": apiKey,
        }),
        getPayload: (model, conversationMessages) => ({
            model,
            messages: conversationMessages,
            max_tokens: 500,
            temperature: 0.7,
        }),
    },
    gemini: {
        endpoint: "https://generativelanguage.googleapis.com/v1beta2/models",
        getHeaders: (apiKey) => ({}), // API key passed in URL
        getPayload: (model, conversationMessages) => {
            const prompt = conversationMessages
                .map((msg) =>
                    msg.role === "user"
                        ? "User: " + msg.content
                        : "Assistant: " + msg.content
                )
                .join("\n");
            return { prompt };
        },
        addApiKeyToUrl: (baseUrl, model, apiKey) =>
            `${baseUrl.replace(/\/+$/, "")}/${model}:generateText?key=${apiKey}`,
    },
};

// ----------------------------------------------
// Main Component: AIwrapperchat
const AIwrapperchat = ({ apiid, onClose }) => {
    // State Management
    const [pageData, setPageData] = useState(null);
    const [initialSystemMessage, setInitialSystemMessage] = useState("");
    const [chatHistory, setChatHistory] = useState([]);
    const [message, setMessage] = useState("");
    const [isThinking, setIsThinking] = useState(false);
    const chatRef = useRef(null);

    const [selectedFile, setSelectedFile] = useState(null);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [showSnackbar, setShowSnackbar] = useState(false);

    const [selectedLanguage, setSelectedLanguage] = useState("English");
    const [selectedDialect, setSelectedDialect] = useState(languages.English[0] || "");

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    // Chat panel loads open by default (for both mobile and desktop)
    const [chatOpen, setChatOpen] = useState(true);

    // Ensure valid dialect when language changes.
    useEffect(() => {
        if (!languages[selectedLanguage].includes(selectedDialect)) {
            setSelectedDialect(languages[selectedLanguage][0]);
        }
    }, [selectedLanguage, selectedDialect]);

    // Fetch Page Data when 'apiid' changes.
    useEffect(() => {
        const fetchPageData = async () => {
            if (!apiid) return;
            try {
                const response = await axios.get(
                    `https://api.swancreate.com/api/llm/sail_ai_pages/${apiid}`
                );
                const data = Array.isArray(response.data)
                    ? response.data[0]
                    : response.data;
                setPageData(data);
                if (data) {
                    const context = `
Your primary role is to assist users by offering insightful and accurate information within the scope of the page�s content. You may utilize your memory for topics relevant to the site�s purpose but must strictly adhere to the page�s subject matter.
Guidelines & Security Measures:
If you context has you impersonate a person, you may use public records to answer questions regarding the person you are impersonating.
- Context Adherence: Stay within the specific context of the current page and avoid unrelated discussions.
- Security Compliance: Do not provide, generate, or reference any site codes, passwords, or confidential information.
- Content Relevance: Ensure all responses directly support the user�s needs as they relate to the content at hand.
Your goal is to deliver valuable, secure, and contextually appropriate assistance to users while maintaining site integrity and security.

Page Context:
Title: ${data.PageTitle || "N/A"}
Description: ${data.PageDescription || "N/A"}
Instructions: ${data.ai_prompt_instructions || "N/A"}
Languages: ${data.ai_languages || "N/A"}
Preferred language/dialect: ${selectedLanguage} - ${selectedDialect}
`;
                    setInitialSystemMessage(context);
                }
            } catch (error) {
                console.error("Error fetching page data:", error.message);
            }
        };

        fetchPageData();

        return () => {
            setPageData(null);
            setChatHistory([]);
        };
    }, [apiid, selectedLanguage, selectedDialect]);

    // Auto-scroll to bottom when chat history updates.
    useEffect(() => {
        if (chatRef.current) {
            chatRef.current.scrollTop = chatRef.current.scrollHeight;
        }
    }, [chatHistory]);

    // Determine the agent logo based on pageData.
    let agentLogo = logoImages["fig_agent.png"]; // default fallback
    if (pageData && pageData.logo_file) {
        const logoKey = pageData.logo_file.trim();
        if (logoImages.hasOwnProperty(logoKey)) {
            agentLogo = logoImages[logoKey];
        } else {
            console.error(`Logo image not found for key: "${logoKey}". Using default.`);
        }
    }

    // Handle Sending Message
    const handleSendMessage = async () => {
        if (!message.trim()) return;
        const userMessage = { sender: "user", message };
        const newChatHistory = [...chatHistory, userMessage];
        setChatHistory(newChatHistory);
        setIsThinking(true);

        try {
            const languageDirective = `Please respond in ${selectedLanguage} - ${selectedDialect}.`;
            const conversationMessages = [
                { role: "system", content: initialSystemMessage },
                { role: "system", content: languageDirective },
                ...newChatHistory.map((msg) => ({
                    role: msg.sender === "user" ? "user" : "assistant",
                    content: msg.message,
                })),
            ];

            let provider = "openai";
            if (pageData && pageData.ai_companyname) {
                const companyName = pageData.ai_companyname.toLowerCase();
                if (companyName.includes("microsoft")) {
                    provider = "azure";
                } else if (companyName.includes("google")) {
                    provider = "gemini";
                } else if (companyName.includes("openai")) {
                    provider = "openai";
                }
            }
            const config = providerConfigs[provider] || providerConfigs["openai"];
            const model = pageData?.ai_model || "gpt-4";
            const apiKey = pageData?.api_key || FALLBACK_API_KEY;
            let endpoint = "";

            if (provider === "gemini") {
                const baseUrl = pageData?.api_base_url || config.endpoint;
                endpoint = config.addApiKeyToUrl(baseUrl, model, apiKey);
            } else {
                endpoint = pageData?.api_base_url || config.endpoint;
            }

            const payload = config.getPayload(model, conversationMessages);

            const response = await axios.post(endpoint, payload, {
                headers: config.getHeaders(apiKey),
            });

            const botResponse = response.data.choices[0].message.content.trim();
            const usageTokens = response.data.usage?.total_tokens || 0;
            try {
                await axios.post("https://api.swancreate.com/api/llm/AI_PromptResponseLog", {
                    SourcePageID: null,
                    SourcePageURL: pageData?.PageTitle || "N/A",
                    UserID: "00000000-0000-0000-0000-000000000001",
                    OrganizationID: "Swan AI Labs",
                    BotID: "sail-wingman",
                    PromptText: message,
                    ResponseText: botResponse,
                    TokenAmount: usageTokens,
                    PromptDate: new Date().toISOString(),
                    ResponseDate: new Date().toISOString(),
                    StorageLocation: null,
                    IsAnalyzed: false,
                    MainCategory: null,
                    SubCategory: null,
                    ipaddress: null,
                });
            } catch (logError) {
                console.error("Error logging prompt/response:", logError);
            }
            setChatHistory((prev) => [...prev, { sender: "bot", message: botResponse }]);
        } catch (error) {
            console.error("Error sending message:", error.message);
            setChatHistory((prev) => [
                ...prev,
                { sender: "bot", message: "Error processing your request." },
            ]);
        } finally {
            setMessage("");
            setIsThinking(false);
        }
    };

    // Handle File Select (for file uploads)
    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
        }
    };

    // Handle File Upload (using OpenAI's endpoint)
    const handleFileUpload = async () => {
        if (!selectedFile) return;
        try {
            setIsThinking(true);
            const formData = new FormData();
            formData.append("file", selectedFile);
            formData.append("purpose", "fine-tune");

            const response = await axios.post("https://api.openai.com/v1/files", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${FALLBACK_API_KEY}`,
                },
            });

            console.log("Upload success:", response.data);
            setSnackbarMessage(`File uploaded! File ID: ${response.data.id}`);
            setShowSnackbar(true);
            setSelectedFile(null);
        } catch (error) {
            console.error("Error uploading file:", error);
            setSnackbarMessage("File upload failed.");
            setShowSnackbar(true);
        } finally {
            setIsThinking(false);
        }
    };

    // Handle Copying Text from a Chat Bubble
    const handleCopyText = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            setSnackbarMessage("Message copied to clipboard!");
            setShowSnackbar(true);
        } catch (err) {
            console.error("Failed to copy text:", err);
        }
    };

    // Handle closing the Snackbar.
    const handleCloseSnackbar = () => {
        setShowSnackbar(false);
        setSnackbarMessage("");
    };

    // ----------------------------------------------
    // RENDER
    return (
        <LayoutContainer>
            {/* IFrame Section */}
            <IFrameContainer>
                {pageData?.PageUrl ? (
                    <iframe
                        src={pageData.PageUrl}
                        style={{ width: "100%", height: "100%", border: "none" }}
                        title={pageData?.PageTitle || "Page Iframe"}
                    />
                ) : (
                    <Typography
                        sx={{
                            margin: "auto",
                            textAlign: "center",
                            fontSize: "16px",
                        }}
                    >
                        Loading page or no page URL provided...
                    </Typography>
                )}
            </IFrameContainer>

            {/* Chat Panel */}
            {isMobile ? (
                // For mobile, use a Drawer.
                <Drawer
                    anchor="right"
                    open={chatOpen}
                    onClose={() => setChatOpen(false)}
                    ModalProps={{ keepMounted: true }}
                >
                    <Box sx={{ width: 350, height: "100%", display: "flex", flexDirection: "column" }}>
                        {/* Close Button */}
                        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
                            <IconButton onClick={() => setChatOpen(false)}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        {/* Pass the isMobile prop so ChatContainer uses 100% height on mobile */}
                        <ChatContainer isMobile={isMobile}>
                            <HeaderContainer>
                                <Logo src={agentLogo} alt="FIG Logo" />
                                <Box
                                    sx={{
                                        textAlign: "center",
                                        backgroundColor: "#F5F5F5",
                                        padding: "12px",
                                        borderRadius: "8px",
                                        boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
                                        marginTop: "8px",
                                        marginBottom: "12px",
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: "bold",
                                            color: "#0277BD",
                                            marginBottom: "4px",
                                        }}
                                    >
                                        The FIG Agent
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: "#333", lineHeight: 1.5 }}>
                                        I am the FIG Agent trained to provide expert guidance on this page.
                                        I can answer detailed questions about the content and help make your
                                        experience more inclusive.
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: "#555", fontStyle: "italic", marginTop: "6px" }}
                                    >
                                        Please select the language in which you would like to discuss this site.
                                    </Typography>
                                </Box>
                                {/* Language + Dialect Selectors */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "row",
                                        width: "100%",
                                        gap: 1,
                                        marginTop: 2,
                                    }}
                                >
                                    <FormControl sx={{ flex: 1 }}>
                                        <InputLabel id="language-select-label">Language</InputLabel>
                                        <Select
                                            labelId="language-select-label"
                                            value={selectedLanguage}
                                            label="Language"
                                            onChange={(e) => setSelectedLanguage(e.target.value)}
                                            sx={{ backgroundColor: "#FFFFFF" }}
                                        >
                                            {Object.keys(languages).map((lang) => (
                                                <MenuItem key={lang} value={lang}>
                                                    {lang}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl sx={{ flex: 1 }}>
                                        <InputLabel id="dialect-select-label">Dialect</InputLabel>
                                        <Select
                                            labelId="dialect-select-label"
                                            value={selectedDialect}
                                            label="Dialect"
                                            onChange={(e) => setSelectedDialect(e.target.value)}
                                            sx={{ backgroundColor: "#FFFFFF" }}
                                        >
                                            {languages[selectedLanguage].map((dialect) => (
                                                <MenuItem key={dialect} value={dialect}>
                                                    {dialect}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            </HeaderContainer>

                            {/* Chat Messages */}
                            <ChatMessages ref={chatRef}>
                                {chatHistory.map((chat, idx) => {
                                    const isUser = chat.sender === "user";
                                    return (
                                        <Box
                                            key={idx}
                                            sx={{
                                                display: "flex",
                                                justifyContent: isUser ? "flex-end" : "flex-start",
                                                mb: 1.5,
                                            }}
                                        >
                                            <ChatBubble isUser={isUser}>
                                                <Box sx={{ flex: 1 }}>{chat.message}</Box>
                                                <Tooltip title="Copy to clipboard">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleCopyText(chat.message)}
                                                        sx={{
                                                            color: isUser ? "#fff" : "#000",
                                                            "&:hover": { opacity: 0.7 },
                                                        }}
                                                    >
                                                        <ContentCopyIcon fontSize="inherit" />
                                                    </IconButton>
                                                </Tooltip>
                                            </ChatBubble>
                                        </Box>
                                    );
                                })}
                            </ChatMessages>

                            {/* Chat Input Area */}
                            <ChatInputArea>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    multiline
                                    rows={2}
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSendMessage();
                                        }
                                    }}
                                    placeholder="Type your message..."
                                    sx={{
                                        mb: 1,
                                        "& .MuiOutlinedInput-root": {
                                            backgroundColor: "#FFFFFF",
                                            fontWeight: "bold",
                                            color: "#00008B",
                                            "& input, & textarea": {
                                                fontWeight: "bold",
                                                color: "#00008B",
                                            },
                                        },
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={handleSendMessage}
                                    sx={{
                                        backgroundColor: "#0288D1",
                                        color: "#fff",
                                        textTransform: "none",
                                        "&:hover": { backgroundColor: "#0277BD" },
                                    }}
                                >
                                    Send
                                </Button>
                            </ChatInputArea>

                            {/* Thinking Overlay */}
                            <Modal open={isThinking}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        height: "100%",
                                        backgroundColor: "rgba(0,0,0,0.5)",
                                    }}
                                >
                                    <CircularProgress sx={{ color: "#FFFFFF" }} />
                                </Box>
                            </Modal>

                            {/* Snackbar for copy/upload messages */}
                            <Snackbar
                                open={showSnackbar}
                                autoHideDuration={3000}
                                onClose={handleCloseSnackbar}
                                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                            >
                                <Alert onClose={handleCloseSnackbar} severity="info" sx={{ width: "100%" }}>
                                    {snackbarMessage}
                                </Alert>
                            </Snackbar>
                        </ChatContainer>
                    </Box>
                </Drawer>
            ) : (
                // For Desktop: show ChatContainer inline if open; otherwise, show a floating circular button.
                <>
                    {chatOpen ? (
                        <Box sx={{ position: "relative" }}>
                            {/* Pass isMobile={isMobile} so the container uses 100vh */}
                            <ChatContainer isMobile={isMobile}>
                                <HeaderContainer>
                                    <Logo src={agentLogo} alt="FIG Logo" />
                                    <Box
                                        sx={{
                                            textAlign: "center",
                                            backgroundColor: "#F5F5F5",
                                            padding: "12px",
                                            borderRadius: "8px",
                                            boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
                                            marginTop: "8px",
                                            marginBottom: "12px",
                                        }}
                                    >
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: "bold",
                                                color: "#0277BD",
                                                marginBottom: "4px",
                                            }}
                                        >
                                            Agent
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: "#333", lineHeight: 1.5 }}>
                                            I am the FIG Agent trained to provide expert guidance on this page.
                                            I can answer detailed questions about the content and help make your
                                            experience more inclusive.
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: "#555", fontStyle: "italic", marginTop: "6px" }}
                                        >
                                            Please select the language in which you would like to discuss this site.
                                        </Typography>
                                    </Box>
                                    {/* Language + Dialect Selectors */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "row",
                                            width: "100%",
                                            gap: 1,
                                            marginTop: 2,
                                        }}
                                    >
                                        <FormControl sx={{ flex: 1 }}>
                                            <InputLabel id="language-select-label">Language</InputLabel>
                                            <Select
                                                labelId="language-select-label"
                                                value={selectedLanguage}
                                                label="Language"
                                                onChange={(e) => setSelectedLanguage(e.target.value)}
                                                sx={{ backgroundColor: "#FFFFFF" }}
                                            >
                                                {Object.keys(languages).map((lang) => (
                                                    <MenuItem key={lang} value={lang}>
                                                        {lang}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl sx={{ flex: 1 }}>
                                            <InputLabel id="dialect-select-label">Dialect</InputLabel>
                                            <Select
                                                labelId="dialect-select-label"
                                                value={selectedDialect}
                                                label="Dialect"
                                                onChange={(e) => setSelectedDialect(e.target.value)}
                                                sx={{ backgroundColor: "#FFFFFF" }}
                                            >
                                                {languages[selectedLanguage].map((dialect) => (
                                                    <MenuItem key={dialect} value={dialect}>
                                                        {dialect}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </HeaderContainer>
                                {/* Close Button for Desktop */}
                                <IconButton
                                    onClick={() => setChatOpen(false)}
                                    sx={{ position: "absolute", top: 8, right: 8 }}
                                >
                                    <CloseIcon />
                                </IconButton>
                                <ChatMessages ref={chatRef}>
                                    {chatHistory.map((chat, idx) => {
                                        const isUser = chat.sender === "user";
                                        return (
                                            <Box
                                                key={idx}
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: isUser ? "flex-end" : "flex-start",
                                                    mb: 1.5,
                                                }}
                                            >
                                                <ChatBubble isUser={isUser}>
                                                    <Box sx={{ flex: 1 }}>{chat.message}</Box>
                                                    <Tooltip title="Copy to clipboard">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleCopyText(chat.message)}
                                                            sx={{
                                                                color: isUser ? "#fff" : "#000",
                                                                "&:hover": { opacity: 0.7 },
                                                            }}
                                                        >
                                                            <ContentCopyIcon fontSize="inherit" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </ChatBubble>
                                            </Box>
                                        );
                                    })}
                                </ChatMessages>
                                <ChatInputArea>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        multiline
                                        rows={2}
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && !e.shiftKey) {
                                                e.preventDefault();
                                                handleSendMessage();
                                            }
                                        }}
                                        placeholder="Type your message..."
                                        sx={{
                                            mb: 1,
                                            "& .MuiOutlinedInput-root": {
                                                backgroundColor: "#FFFFFF",
                                                fontWeight: "bold",
                                                color: "#00008B",
                                                "& input, & textarea": {
                                                    fontWeight: "bold",
                                                    color: "#00008B",
                                                },
                                            },
                                        }}
                                    />
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={handleSendMessage}
                                        sx={{
                                            backgroundColor: "#0288D1",
                                            color: "#fff",
                                            textTransform: "none",
                                            "&:hover": { backgroundColor: "#0277BD" },
                                        }}
                                    >
                                        Send
                                    </Button>
                                </ChatInputArea>
                                <Modal open={isThinking}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            height: "100%",
                                            backgroundColor: "rgba(0,0,0,0.5)",
                                        }}
                                    >
                                        <CircularProgress sx={{ color: "#FFFFFF" }} />
                                    </Box>
                                </Modal>
                                <Snackbar
                                    open={showSnackbar}
                                    autoHideDuration={3000}
                                    onClose={handleCloseSnackbar}
                                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                                >
                                    <Alert onClose={handleCloseSnackbar} severity="info" sx={{ width: "100%" }}>
                                        {snackbarMessage}
                                    </Alert>
                                </Snackbar>
                            </ChatContainer>
                        </Box>
                    ) : (
                        // When chat is retracted, show a floating circular button with the agent logo.
                        <Box sx={{ position: "fixed", right: 16, bottom: 16, zIndex: 1300 }}>
                            <IconButton
                                onClick={() => setChatOpen(true)}
                                sx={{ p: 0, borderRadius: "50%", width: 56, height: 56 }}
                            >
                                <Box
                                    component="img"
                                    src={agentLogo}
                                    sx={{ borderRadius: "50%", width: "100%", height: "100%" }}
                                />
                            </IconButton>
                        </Box>
                    )}
                </>
            )}
        </LayoutContainer>
    );
};

export default AIwrapperchat;
